/**
 * Employee Training View — shows assigned training guides loaded from DB
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, BookOpen, ChevronDown, ChevronUp, Package, Tag,
  FileText, FolderOpen, Monitor, ShoppingBag, Loader2, ArrowLeft, X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Package, Camera, FileText, Tag, FolderOpen, Monitor, ShoppingBag, BookOpen
};

/** Full-screen image lightbox overlay */
function ImageLightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <div className="relative max-w-3xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm flex items-center gap-1 transition-colors"
          data-testid="lightbox-close-btn"
        >
          <X className="w-5 h-5" /> Close
        </button>
        <img
          src={src}
          alt="Reference guide"
          className="w-full h-auto max-h-[85vh] object-contain rounded-xl shadow-2xl"
        />
      </div>
    </div>
  );
}

/** Render a step string with basic formatting: **bold**, _italic_, !!important!!, [IMG:url] */
function FormattedStep({ text, onImageClick }) {
  // Check for image marker: [IMG:url]
  const imgMatch = text.match(/^\[IMG:(.*?)\]$/);
  if (imgMatch) {
    return (
      <li className="list-none">
        <img
          src={imgMatch[1]}
          alt="Reference guide"
          className="w-full max-w-md rounded-lg border border-white/20 shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => onImageClick?.(imgMatch[1])}
          data-testid="training-reference-image"
        />
        <span className="text-[10px] text-white/40 mt-1 block">Tap image to enlarge</span>
      </li>
    );
  }

  const isBullet = text.startsWith("•");
  const isImportant = text.includes("!!");
  const cleanText = text.replace(/^•\s*/, "");

  // Parse **bold**, _italic_, !!important!! markers
  const parts = cleanText.split(/(\*\*[^*]+\*\*|_[^_]+_|!![^!]+!!)/g);
  const rendered = parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("_") && part.endsWith("_")) {
      return <em key={i} className="italic text-white/80 bg-white/5 px-1 rounded">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("!!") && part.endsWith("!!")) {
      return <strong key={i} className="font-bold text-red-400">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });

  if (isBullet) {
    return (
      <li className={`text-xs flex items-start gap-2 ml-2 ${isImportant ? "text-red-300/90" : "text-white/70"}`}>
        <span className="text-white/40 mt-0.5">&#8226;</span>
        <span>{rendered}</span>
      </li>
    );
  }

  return (
    <li className={`text-xs flex items-start gap-2 ${isImportant ? "text-red-300/90 font-medium" : "text-white/70"}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-white/30 mt-1.5 flex-shrink-0" />
      <span>{rendered}</span>
    </li>
  );
}

function StepSection({ section, color, onImageClick }) {
  const IconComp = ICON_MAP[section.icon] || FileText;
  return (
    <div className="bg-white/5 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <IconComp className="w-4 h-4" style={{ color }} />
        <h4 className="font-medium text-sm text-white/90">{section.title}</h4>
      </div>
      <ul className="space-y-1.5 ml-6">
        {section.steps.map((step, j) => (
          <FormattedStep key={j} text={step} onImageClick={onImageClick} />
        ))}
      </ul>
    </div>
  );
}

function GuideCard({ guide, color, defaultOpen }) {
  const [expanded, setExpanded] = useState(defaultOpen || false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const IconComp = guide.guide_type === "photography" ? Camera : BookOpen;

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden">
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors"
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}25` }}>
          <IconComp className="w-4.5 h-4.5" style={{ color }} />
        </div>
        <span className="font-medium text-sm text-white flex-1 text-left">{guide.title}</span>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              {guide.sections.map((section, i) => (
                <StepSection key={i} section={section} color={color} onImageClick={setLightboxSrc} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function EmployeeTrainingView({ getAuthHeader, onBack, viewEmployeeId }) {
  const [assignments, setAssignments] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const guidesPromise = axios.get(`${API}/training-content/`, getAuthHeader());

        let assignData;
        if (viewEmployeeId) {
          // Admin viewing specific employee — fetch all and filter
          const allRes = await axios.get(`${API}/training-assignments/`, getAuthHeader());
          assignData = (allRes.data.assignments || []).filter(a => a.employee_id === viewEmployeeId);
        } else {
          const myRes = await axios.get(`${API}/training-assignments/my`, getAuthHeader());
          assignData = myRes.data.assignments || [];
        }

        const guidesRes = await guidesPromise;
        setAssignments(assignData);
        setGuides(guidesRes.data.guides || []);
      } catch {
        setAssignments([]);
        setGuides([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const hasPhotography = assignments.some(a => a.training_type === "photography");
  const hasListing = assignments.some(a => a.training_type === "listing");
  const photoGuide = guides.find(g => g.guide_type === "photography");
  const listingGuide = guides.find(g => g.guide_type === "listing");
  const draftsGuide = guides.find(g => g.guide_type === "vendoo_drafts");

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="employee-training-view">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors"
        data-testid="training-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      {hasPhotography && photoGuide && (
        <GuideCard
          guide={photoGuide}
          color="#F59E0B"
          defaultOpen={true}
        />
      )}

      {hasListing && draftsGuide && (
        <GuideCard
          guide={draftsGuide}
          color="#8B5CF6"
          defaultOpen={!hasPhotography}
        />
      )}

      {hasListing && listingGuide && (
        <GuideCard
          guide={listingGuide}
          color="#3B82F6"
          defaultOpen={false}
        />
      )}

      {!hasPhotography && !hasListing && (
        <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 text-center">
          <BookOpen className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <p className="text-white/60 text-sm">No training assigned yet</p>
        </div>
      )}
    </div>
  );
}
